import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
	Button,
	Datepicker,
	DynamicIconWrapper,
	Icons,
	Modal,
	RadioButton,
	Select,
	SpinnerOverlay,
	SubmissionButtons,
} from '@components/common';
import FlatTree from '@components/common/FlatTree';
import { TFlatTreeSubmitHandler } from '@components/common/FlatTree/FlatTree';
import {
	TSetGoldenSettingsConfig,
} from '@components/routes/CmModule/ConsistencyCheck/GoldenSettingsForm/AttachGoldenSettings/AttachGoldenSettings';

import useGoldenSettings from '@hooks/goldenSettings/useGoldenSetting';
import useMutateGoldenSettingsByPath from '@hooks/goldenSettings/useMutateGoldenSettingsByPath';
import useMutateGoldenSettingsExport from '@hooks/goldenSettings/useMutateGoldenSettingsExport';
import useMutateGoldenValidationRules from '@hooks/goldenSettings/useMutateGoldenValidationRules';

import { dateFormattingUTCDayMonthYear, formatDate } from '@utils/date';
import { startOfDayInUTC } from '@utils/kpi-utils/date';
import {
	convertToMavenirFlatTreeData,
	createMoTreeFromFlatMavenirTree,
	filterCheckedNodes,
} from '@utils/treeFormatter/treeFormatter';
import { convertToFlatTreeData, createObjectFromFlatArray } from '@components/common/FlatTree/utils';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IFlatMoTreeNode, IMoTreeInitial } from '@interfaces/components/tree';
import { TFlatTreeGetValidation } from '@components/common/FlatTree/interface';

import { TNodeTypeValues, TTechnologiesValues, TVendorsValues } from '@enums/common';
import { EValidationMode } from '@enums/validationMode';
import { EGoldenSettingsFields } from './enums';

import {
	goldenSettingsCheckboxTreeModalWidth,
	goldenSettingsTModalHeight,
	goldenSettingsTreeModalWidth,
	iconColorProps,
	importGoldenSettingsModalWidth,
	responsePathSeparator,
	treeWrapperKey,
} from './constants';

import FlatTreeMavenir from '../../ConfigurationComparison/MOTreeMavenir/FlatTreeMavenir';
import { getMoSkippedParameters } from '../../ConfigurationComparison/MOTreeMavenir/utils';
import AttachGoldenSettings from './AttachGoldenSettings';
import GoldenSettingsLabelInfo from './GoldenSettingsLabelInfo';
import ImportedGoldenSettingsFile from './ImportedGoldenSettingsFile';

import styles from './GoldenSettingsForm.module.scss';

interface GoldenSettingsFormProps {
	nodeType: TNodeTypeValues;
	vendor: TVendorsValues;
	technology: TTechnologiesValues;
	type: string;
	version: string;
	isMavenirVendor: boolean;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	closeGoldenSettingForm: () => void;
	onClose: () => void;
	nodeOptions?: TSelectOption<string>[];
	nodes: string[];
}

interface IInstalledConfig {
	rawData: string;
	tree: IFlatMoTreeNode[];
	skippedParameters: string[];
	siteId: string | undefined;
	importFileName?: string;
	isImported: boolean;
	id: string | undefined;
	date: number | null;
}

interface ILoadedConfig {
	rawData: string;
	tree: IFlatMoTreeNode[];
	importId?: string;
}

interface IGoldenSettingsFormFields {
	[EGoldenSettingsFields.selectValue]: TSelectOption<string> | null;
	[EGoldenSettingsFields.date]: number | null;
	[EGoldenSettingsFields.isImport]: boolean;
}

export interface IGoldenSettingsImportFile {
	rawData: string;
	importFileName: string;
	importId: string;
	tree: IMoTreeInitial;
}

const GoldenSettingsForm = ({
	nodeType,
	technology,
	nodes,
	vendor,
	version,
	type,
	nodeOptions,
	isMavenirVendor = false,
	setIsDirtyFormModal,
	closeGoldenSettingForm,
	onClose,
}: GoldenSettingsFormProps) => {
	const {
		formState: { errors, isDirty },
		watch,
		control,
		setValue,
	} = useForm<IGoldenSettingsFormFields>({
		mode: EValidationMode.onSubmit,
		defaultValues: {
			[EGoldenSettingsFields.selectValue]: null,
			[EGoldenSettingsFields.date]: null,
			[EGoldenSettingsFields.isImport]: false,
		},
	});

	const settingsData = useMemo(() => {
		return {
			nodeType,
			technology,
			vendor,
			version,
			nodes,
			type,
		};
	}, [nodeType, technology, vendor, version, nodes, type]);

	const selectValueText = isMavenirVendor ? 'Node ID' : 'Site ID';
	const actionButtonText = isMavenirVendor ? 'Set Golden Settings' : 'Use as Golden Settings';

	const [loadedConfig, setLoadedConfig] = useState<ILoadedConfig | null>(null);
	const [installedConfig, setInstalledConfig] = useState<IInstalledConfig | null>(null);
	const [importedFileName, setImportedFileName] = useState<string | undefined>();
	const [isTreeChanged, setIsTreeChanged] = useState(false);
	const [isConfigChanged, setIsConfigChanged] = useState(false);
	const [isSetGoldenSettingsModal, setIsSetGoldenSettingsModal] = useState(false);

	const [isOpenUploadFilesModal, setIsOpenUploadFilesModal] = useState(false);
	const [isTreeOpen, setIsTreeOpen] = useState(false);

	const toggleUploadFilesModal = useCallback(() => setIsOpenUploadFilesModal((prevState) => !prevState), []);
	const openSetGoldenSettingsModal = useCallback(() => setIsSetGoldenSettingsModal(true), []);
	const closeSetGoldenSettingsModal = useCallback(() => setIsSetGoldenSettingsModal(false), []);
	const openTreeModal = useCallback(() => setIsTreeOpen(true), []);
	const closeTreeModal = useCallback(() => setIsTreeOpen(false), []);

	const setDate = useCallback((date: number | null) => setValue(EGoldenSettingsFields.date, date), [setValue]);

	const date = watch(EGoldenSettingsFields.date);
	const selectValue = watch(EGoldenSettingsFields.selectValue)?.value;
	const isImport = watch(EGoldenSettingsFields.isImport);

	const areOptionsSelected = !!date && !!selectValue;

	const {
		editGoldenSettingsConfig,
		resetGoldenSettingsConfig,
		setGoldenSettingsConfig,
		rawConfigDataOnSelectedDate,
		goldenSettingsConfig,
		goldenSettingsOnSelectedDate,
		goldenSettingsInfo,
		goldenSettingsDates,
		goldenSettingsSites,
		isLoadingGoldenSettingsDates,
		isLoadingGoldenSettingsSites,
		isLoadingGoldenSettings,
	} = useGoldenSettings({
		...settingsData,
		siteId: selectValue,
		date,
		path: undefined,
		isMavenirVendor,
		closeGoldenSettingForm,
	});

	const { handleGoldenSettingsExport } = useMutateGoldenSettingsExport();

	const { getValidationRules, isLoadingValidationRules } = useMutateGoldenValidationRules(vendor);

	const { getConfigForPath, isLoadingGetConfigForPath } = useMutateGoldenSettingsByPath({
		importId: loadedConfig?.importId,
		dateText: date ? formatDate(date) : undefined,
		...settingsData,
	});

	const handleExport = useCallback(() => {
		return handleGoldenSettingsExport(settingsData);
	}, [
		handleGoldenSettingsExport,
		settingsData,
	]);

	const isSubmitDisabled = !(
		(goldenSettingsConfig && isTreeChanged)
		|| (goldenSettingsConfig && !installedConfig)
		|| isConfigChanged
	);

	const isSetAsGoldenSettingsDisabled = isMavenirVendor && !loadedConfig?.tree?.length || !loadedConfig;

	const isSelectDisabled = isLoadingGoldenSettingsSites || !!importedFileName || isImport;
	const isLoadingForm = isLoadingGoldenSettingsDates || isLoadingGoldenSettingsSites
		|| isLoadingGoldenSettings;

	useEffect(() => {
		setImportedFileName(undefined);
		setLoadedConfig(null);
		setValue(EGoldenSettingsFields.date, null);
		setValue(EGoldenSettingsFields.selectValue, null);
	}, [isImport, setValue]);

	useEffect(() => {
		if (goldenSettingsConfig && goldenSettingsInfo) {
			const tree = goldenSettingsConfig.tree;

			setInstalledConfig({
				...goldenSettingsInfo,
				rawData: goldenSettingsConfig.rawData,
				skippedParameters: goldenSettingsConfig.skippedParameters,
				date: goldenSettingsInfo.date,
				tree: tree ?
					convertToMavenirFlatTreeData({
						data: tree ? [tree] : [],
						isSingleSelectMode: true,
						isDefaultNodeChecked: true,
						isMoIdCheckbox: true,
						skippedPaths: goldenSettingsConfig.skippedParameters,
					})
					: [],
			});
		}
	}, [goldenSettingsConfig, goldenSettingsInfo]);

	useEffect(() => {
		if (goldenSettingsOnSelectedDate && areOptionsSelected) {
			if (isMavenirVendor && typeof goldenSettingsOnSelectedDate === 'object') {
				setLoadedConfig({
					tree: goldenSettingsOnSelectedDate,
					rawData: '',
				});
			} else {
				setLoadedConfig({
					rawData: JSON.stringify(goldenSettingsOnSelectedDate),
					tree: [],
				});
				setImportedFileName(undefined);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		rawConfigDataOnSelectedDate,
		areOptionsSelected,
		isMavenirVendor,
	]);

	useEffect(() => setValue(EGoldenSettingsFields.date, null), [selectValue, setValue]);

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	const installedGoldenSettingsDate = useMemo(() => {
		return installedConfig?.date ? dateFormattingUTCDayMonthYear(installedConfig.date) : undefined;
	}, [installedConfig?.date]);

	const treeData = useMemo(() => {
		if (installedConfig?.tree && isMavenirVendor) {
			return installedConfig.tree.map((item, key) => ({ ...item, order: key }));
		}

		if (installedConfig?.rawData && installedConfig?.skippedParameters) {
			try {
				return convertToFlatTreeData(
					JSON.parse(installedConfig.rawData),
					installedConfig.skippedParameters,
					treeWrapperKey,
					responsePathSeparator,
				);
			} catch {
				return null;
			}

		}

		return null;
	}, [installedConfig, isMavenirVendor]);

	const getCheckboxLabel = useCallback((value: boolean) => (value ? 'Unskipped' : 'Skipped'), []);

	const handleRemoveFile = useCallback(() => {
		setLoadedConfig(null);
		setImportedFileName(undefined);
	}, []);

	const setImportedConfig = useCallback(({
		rawData,
		importFileName,
		tree,
		importId,
	}: IGoldenSettingsImportFile) => {
		setImportedFileName(importFileName);
		setLoadedConfig({
			importId,
			rawData: rawData,
			tree: convertToMavenirFlatTreeData({
				data: tree ? [tree] : [],
				isSingleSelectMode: true,
				isDefaultNodeChecked: true,
				isMoIdCheckbox: true,
			}),
		});
	}, []);

	const resetConfigHandler = useCallback(() => {
		setValue(EGoldenSettingsFields.selectValue, null);
		setValue(EGoldenSettingsFields.date, null);
		setLoadedConfig(null);
		setInstalledConfig(null);
		setImportedFileName(undefined);
		setIsDirtyFormModal(true);
	}, [setValue, setIsDirtyFormModal]);

	const submitConfig = () => {
		const dto = {
			...installedConfig,
			...settingsData,
			dateText: installedConfig?.date ? formatDate(installedConfig.date) : undefined,
			tree: isMavenirVendor && installedConfig
				? createMoTreeFromFlatMavenirTree(installedConfig.tree)
				: undefined,
		};

		switch (true) {
			case installedConfig && !installedConfig?.id:
				setGoldenSettingsConfig(dto);
				break;
			case !!installedConfig?.id:
				editGoldenSettingsConfig(dto);
				break;
			case !installedConfig && !!goldenSettingsConfig:
				resetGoldenSettingsConfig(settingsData);
				break;
			default:
				break;
		}
	};

	const submitTree = useCallback((data: IFlatMoTreeNode[]) => {
		if (!installedConfig) return;

		const { result = {}, skippedFields = [] } = !isMavenirVendor
			? createObjectFromFlatArray(data, treeWrapperKey)
			: {};

		const skippedParameters = isMavenirVendor ? getMoSkippedParameters(data) : skippedFields;

		setInstalledConfig((prev) => {
			return prev
				? {
					...prev,
					tree: data,
					rawData: JSON.stringify(result),
					skippedParameters,
				} : null;
		});

		setIsTreeChanged(true);
		setIsDirtyFormModal(true);
		closeTreeModal();
	}, [
		isMavenirVendor,
		setInstalledConfig,
		closeTreeModal,
		setIsDirtyFormModal,
		installedConfig,
	]);

	const setAsGoldenSettings = useCallback((data?: IFlatMoTreeNode[]) => {
		if (!data && isMavenirVendor || !isMavenirVendor && !loadedConfig) {
			return;
		}

		const utcDate = date ? startOfDayInUTC(new Date(date)) : startOfDayInUTC(new Date());

		setInstalledConfig({
			importFileName: importedFileName,
			isImported: !!importedFileName,
			rawData: loadedConfig?.rawData || '',
			skippedParameters: [],
			siteId: selectValue,
			tree: data ? filterCheckedNodes([...data]) : [],
			date: utcDate,
			id: undefined,
		});

		setLoadedConfig(null);
		setImportedFileName(undefined);
		setIsConfigChanged(true);
		setIsDirtyFormModal(true);
		setValue(EGoldenSettingsFields.selectValue, null);
		setValue(EGoldenSettingsFields.date, null);
		closeSetGoldenSettingsModal();
	}, [
		selectValue,
		date,
		importedFileName,
		setValue,
		setIsDirtyFormModal,
		setInstalledConfig,
		closeSetGoldenSettingsModal,
		loadedConfig,
		isMavenirVendor,
	]);

	const useAsGoldenSettings = useCallback(() => {
		if (!loadedConfig) {
			return;
		}

		if (isMavenirVendor) {
			openSetGoldenSettingsModal();

			return;
		}

		setAsGoldenSettings();
	}, [
		loadedConfig,
		openSetGoldenSettingsModal,
		setAsGoldenSettings,
		isMavenirVendor,
	]);

	const [data, setData] = useState<IFlatMoTreeNode[]>([]);

	useEffect(() => {
		if (loadedConfig?.tree) {
			setData(loadedConfig.tree);
		}
	}, [loadedConfig?.tree]);

	return (
		<div className={styles.goldenSettingsFormWrapper}>
			{isLoadingForm && <SpinnerOverlay />}
			<Modal
				isOpen={isOpenUploadFilesModal}
				isScrollableContent
				onClose={toggleUploadFilesModal}
				title='Upload file'
				width={importGoldenSettingsModalWidth}
			>
				<AttachGoldenSettings
					isMavenirVendor={isMavenirVendor}
					params={settingsData}
					setGoldenSettingsConfig={setImportedConfig as TSetGoldenSettingsConfig}
					toggleUploadFilesModal={toggleUploadFilesModal}
				/>
			</Modal>
			<Modal
				height={goldenSettingsTModalHeight}
				isOpen={!!(isSetGoldenSettingsModal && loadedConfig?.tree)}
				onClose={closeSetGoldenSettingsModal}
				title='Set Golden Settings'
				width={goldenSettingsCheckboxTreeModalWidth}
			>
				<FlatTreeMavenir
					cancelHandler={closeSetGoldenSettingsModal}
					cancelText='Cancel'
					data={data}
					getCheckboxLabel={getCheckboxLabel}
					isLoading={isLoadingGetConfigForPath}
					isReadOnly={false}
					isSingleSelectMode
					setData={setData}
					submitHandler={setAsGoldenSettings}
					submitText='Submit'
					updateConfig={getConfigForPath}
				/>
			</Modal>
			{isTreeOpen && treeData
				&& (
					<Modal
						height={goldenSettingsTModalHeight}
						isOpen={!!(isTreeOpen && treeData)}
						onClose={closeTreeModal}
						title='Edit Golden Settings'
						width={goldenSettingsTreeModalWidth}
					>
						<FlatTree
							cancelHandler={closeTreeModal}
							getValidationRules={(
								isMavenirVendor
									? getValidationRules
									: undefined
							) as unknown as TFlatTreeGetValidation}
							isLoading={isLoadingValidationRules}
							isReadOnly={false}
							submitHandler={submitTree as TFlatTreeSubmitHandler}
							treeData={treeData}
						/>
					</Modal>
				)
			}
			<div className={styles.goldenSettingsFormContent}>
				<div className={styles.header}>
					<GoldenSettingsLabelInfo
						date={installedGoldenSettingsDate}
						importFileName={installedConfig?.importFileName}
					/>
					<div className={styles.actionWrapper}>
						<DynamicIconWrapper
							icon={Icons.export}
							isDisabled={!goldenSettingsConfig}
							onClick={handleExport}
							tooltipLabel='Export Golden Settings'
						/>
						<DynamicIconWrapper
							icon={Icons.editIcon}
							isDisabled={!installedConfig}
							onClick={openTreeModal}
							tooltipLabel='Edit Golden Settings'
						/>
					</div>
				</div>
				<span className={styles.grayBorder} />
				<span className={styles.title}>Golden Settings Source</span>
				<div className={styles.selectDateWrapper}>
					<RadioButton
						checked={!isImport}
						id='isSelectDate'
						label={`${selectValueText} and date`}
						name={EGoldenSettingsFields.isImport}
						setValue={setValue}
						value={false}
						withUseForms
					/>
					<div className={styles.selectDateControl}>
						<Select
							control={control}
							disabled={isSelectDisabled}
							errorMessage={errors.selectValue?.message}
							isClearable
							label={selectValueText}
							name={EGoldenSettingsFields.selectValue}
							options={nodeOptions?.length ? nodeOptions : goldenSettingsSites}
							placeholder={`Select ${selectValueText}`}
						/>
						<Datepicker
							disableInput
							disabled={isSelectDisabled || !selectValue}
							handleChange={setDate}
							includeDates={goldenSettingsDates}
							isClearable
							isMobileDevice={false}
							isTodayButton={false}
							label='Configuration date'
							selected={date ? new Date(date) : null}
						/>
					</div>
				</div>
				<div className={styles.configurationImportFileWrapper}>
					<RadioButton
						checked={isImport}
						id='isImportFile'
						label='File:'
						name={EGoldenSettingsFields.isImport}
						setValue={setValue}
						value
						withUseForms
					/>
					{importedFileName
						? (
							<ImportedGoldenSettingsFile
								fileName={importedFileName}
								removeHandler={handleRemoveFile}
							/>
						)
						: (
							<Button
								disabled={!isImport}
								icon={Icons.import(iconColorProps)}
								onClick={toggleUploadFilesModal}
								text='Import File'
								withoutBackground
							/>
						)
					}
				</div>
				<div className={styles.setAsGoldenSettingsWrapper}>
					<Button
						disabled={isSetAsGoldenSettingsDisabled}
						large
						onClick={useAsGoldenSettings}
						secondary
						text={actionButtonText}
					/>
				</div>
			</div>
			<div className={styles.buttonsWrapper}>
				<Button
					disabled={!installedConfig}
					icon={Icons.resetIcon(iconColorProps)}
					onClick={resetConfigHandler}
					text='Reset'
					withoutBackground
				/>
				<SubmissionButtons
					disabledPositive={isSubmitDisabled}
					negativeHandler={onClose}
					positiveHandler={submitConfig}
					positiveLabel='Save'
				/>
			</div>
		</div>
	);
};

export default GoldenSettingsForm;

